/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetAuthClientRequest,
  GetAuthClientSecurity,
} from "../models/operations/getauthclient.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AuthClientQueryData,
  buildAuthClientQuery,
  prefetchAuthClient,
  queryKeyAuthClient,
} from "./authClient.core.js";
export {
  type AuthClientQueryData,
  buildAuthClientQuery,
  prefetchAuthClient,
  queryKeyAuthClient,
};

export type AuthClientQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getAuthClient authClients
 *
 * @remarks
 * Get an auth client in the caller's organization. Requires org:read.
 */
export function useAuthClient(
  request: GetAuthClientRequest,
  security?: GetAuthClientSecurity | undefined,
  options?: QueryHookOptions<AuthClientQueryData, AuthClientQueryError>,
): UseQueryResult<AuthClientQueryData, AuthClientQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAuthClientQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getAuthClient authClients
 *
 * @remarks
 * Get an auth client in the caller's organization. Requires org:read.
 */
export function useAuthClientSuspense(
  request: GetAuthClientRequest,
  security?: GetAuthClientSecurity | undefined,
  options?: SuspenseQueryHookOptions<AuthClientQueryData, AuthClientQueryError>,
): UseSuspenseQueryResult<AuthClientQueryData, AuthClientQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAuthClientQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAuthClientData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: AuthClientQueryData,
): AuthClientQueryData | undefined {
  const key = queryKeyAuthClient(...queryKeyBase);

  return client.setQueryData<AuthClientQueryData>(key, data);
}

export function invalidateAuthClient(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "get", ...queryKeyBase],
  });
}

export function invalidateAllAuthClient(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "authClients", "get"],
  });
}
